import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { pt } from "date-fns/locale";
import { WEEK_DAY_OPTIONS } from "../../../utils/reservedSlotsConstants";
import { PSY_SERVICE } from "../../../utils/constants";
import { Clinic, TherapistUser, TherapistJunction, SlotFormData } from "../types";
import { useServiceTimes } from "../../../api/useServiceTimes";

type SlotFormProps = {
    form: SlotFormData;
    setForm: (form: SlotFormData) => void;
    disabled: boolean;
    clinics: Array<Clinic>;
    serviceId: number | undefined;
    therapistsFromTreatment: Array<TherapistJunction>;
    isWeekly: boolean;
    isBiweekly: boolean;
};

export const SlotForm = ({
    form,
    setForm,
    disabled,
    clinics,
    serviceId,
    therapistsFromTreatment,
    isWeekly,
    isBiweekly,
}: SlotFormProps) => {
    const isPsyService = serviceId === PSY_SERVICE;
    const { data: serviceTimesData } = useServiceTimes();

    const getAvailableTimes = (weekDay: number | "") => {
        if (weekDay === "" || !serviceTimesData) return [];
        
        const isSaturday = weekDay === 6;
        
        if (isPsyService) {
            return serviceTimesData.psyAllowedTimes;
        }
        
        const nfbTimes = isSaturday
            ? serviceTimesData.nfbAllowedTimesSaturdayNew
            : serviceTimesData.nfbAllowedTimesWeekNew;
        return nfbTimes;
    };

    return (
        <>
            <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Clínica</InputLabel>
                <Select
                    value={form.clinic}
                    label="Clínica"
                    disabled={disabled}
                    onChange={(e) => setForm({ ...form, clinic: e.target.value as number })}
                >
                    {clinics.map((clinic) => (
                        <MenuItem key={clinic.id} value={clinic.id}>{clinic.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Dia da Semana</InputLabel>
                <Select
                    value={form.week_day}
                    label="Dia da Semana"
                    disabled={disabled}
                    onChange={(e) => setForm({ ...form, week_day: e.target.value as number, time: "" })}
                >
                    {WEEK_DAY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }} disabled={form.week_day === "" || disabled}>
                <InputLabel>Hora</InputLabel>
                <Select
                    value={form.time}
                    label="Hora"
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                >
                    {getAvailableTimes(form.week_day).map((time) => (
                        <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {isPsyService && therapistsFromTreatment.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Terapeuta</InputLabel>
                    <Select
                        value={form.therapist?.id ?? ""}
                        label="Terapeuta"
                        disabled={disabled}
                        onChange={(e) => {
                            const selectedUserId = e.target.value as string;
                            const selectedJunction = therapistsFromTreatment.find(
                                (junction) => junction.technicians_id.user_id.id === selectedUserId
                            );
                            const userInfo = selectedJunction?.technicians_id.user_id;
                            const therapistUser: TherapistUser | undefined = userInfo
                                ? { id: userInfo.id, first_name: userInfo.first_name, last_name: userInfo.last_name }
                                : undefined;
                            setForm({ ...form, therapist: therapistUser });
                        }}
                    >
                        {therapistsFromTreatment.map((junction) => {
                            const user = junction.technicians_id.user_id;
                            return (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.first_name} {user.last_name}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            )}

            {isWeekly && (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pt}>
                    <DatePicker
                        label="Data Âncora Semanal"
                        value={form.weekly_anchor_date}
                        disabled={disabled}
                        onChange={(newValue) => setForm({ ...form, weekly_anchor_date: newValue })}
                        slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                        }}
                    />
                </LocalizationProvider>
            )}

            {isBiweekly && (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pt}>
                    <DatePicker
                        label="Data Âncora Quinzenal"
                        value={form.biweekly_anchor_date}
                        disabled={disabled}
                        onChange={(newValue) => setForm({ ...form, biweekly_anchor_date: newValue })}
                        slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                        }}
                    />
                </LocalizationProvider>
            )}
        </>
    );
};

